<!-- Displays a list of password requirements, and whether or not the password meets those requirements.
@prop password - A String to test against the password requirements.
@emit passwordValid - A boolean that emits true if the password is valid and false if not. -->
<script setup lang="ts">
import { ref, watch, defineEmits, toRef } from "vue";
import type { Ref } from "vue";

const emit = defineEmits(["passwordValid"]);
const props = defineProps<{
    password: string;
}>();
const hasUppercase: Ref<boolean> = ref(false);
const hasLowercase: Ref<boolean> = ref(false);
const hasSpecialChar: Ref<boolean> = ref(false);
const hasNumber: Ref<boolean> = ref(false);
const hasLength: Ref<boolean> = ref(false);
const passwordRef = toRef(props, "password");

function validatePassword() {
    // Validate uppercase
    hasUppercase.value = /[A-Z]/.test(props.password);

    // Validate lowercase
    hasLowercase.value = /[a-z]/.test(props.password);

    // Validate special character
    hasSpecialChar.value = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(props.password);

    // Validate number
    hasNumber.value = /[0-9]/.test(props.password);

    // Validate length
    hasLength.value = props.password.length >= 8;
}

watch(passwordRef, async () => {
    validatePassword();

    if (hasUppercase.value && hasLowercase.value && hasSpecialChar.value && hasNumber.value && hasLength.value) {
        emit("passwordValid", true);
    } else {
        emit("passwordValid", false);
    }
});
</script>

<template>
    <v-container class="pa-0">
        <v-row no-gutters>
            <v-col cols="12">
                <div class="d-flex align-center mb-2">
                    <v-icon :color="hasUppercase ? 'green' : 'red'" class="mr-2">
                        {{ hasUppercase ? "mdi-check" : "mdi-close" }}
                    </v-icon>
                    <span>Contains at least 1 uppercase letter</span>
                </div>

                <div class="d-flex align-center mb-2">
                    <v-icon :color="hasLowercase ? 'green' : 'red'" class="mr-2">
                        {{ hasLowercase ? "mdi-check" : "mdi-close" }}
                    </v-icon>
                    <span>Contains at least 1 lowercase letter</span>
                </div>

                <div class="d-flex align-center mb-2">
                    <v-icon :color="hasSpecialChar ? 'green' : 'red'" class="mr-2">
                        {{ hasSpecialChar ? "mdi-check" : "mdi-close" }}
                    </v-icon>
                    <span>Contains at least 1 special character</span>
                </div>

                <div class="d-flex align-center mb-2">
                    <v-icon :color="hasNumber ? 'green' : 'red'" class="mr-2">
                        {{ hasNumber ? "mdi-check" : "mdi-close" }}
                    </v-icon>
                    <span>Contains at least 1 number</span>
                </div>

                <div class="d-flex align-center">
                    <v-icon :color="hasLength ? 'green' : 'red'" class="mr-2">
                        {{ hasLength ? "mdi-check" : "mdi-close" }}
                    </v-icon>
                    <span>Is at least 8 characters long</span>
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<style scoped>
.v-container {
    background-color: white;
    color: black;
}
</style>
